"use client";

import { useRouter } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { JobProgress } from "@/components/jobs/job-progress";
import { useShell } from "@/components/shell/shell-context";
import { ApiError, api, apiDownload, loginUrl } from "@/lib/api/client";
import type {
  Job,
  Project,
  PromptBriefingStarted,
  PromptInsights,
  PromptItem,
  PromptJob,
  PromptRequirements,
  PromptRun,
  PromptStatus,
  PromptTopic,
} from "@/lib/api/types";
import { saveBlob } from "@/lib/download";
import { formatCount, formatDateTime } from "@/lib/format";
import {
  type Filters,
  NO_FILTERS,
  NO_TAG,
  RUN_KIND_LABEL,
  STATUS_LABEL,
  STATUS_ORDER,
  type Sort,
  type SortKey,
  counts,
  filterItems,
  nextSort,
  ofStatus,
  runIsOpen,
  sortItems,
  tagOptions,
} from "@/lib/prompt-module";

import { BriefingDialog } from "./briefing-dialog";
import { ClusterDialog } from "./cluster-dialog";
import { DemandDialog } from "./demand-dialog";
import { type InsightsStatus, InsightsPanel } from "./insights-panel";
import { GenerateBar, type GenerateRequest } from "./generate-bar";
import { PromptDetail } from "./prompt-detail";
import { PromptTable } from "./prompt-table";
import { RunsPanel } from "./runs-panel";
import { TriggerDialog } from "./trigger-dialog";

type RunningJob = { id: number; title: string };
type Panel = "none" | "generate" | "trigger" | "cluster" | "demand" | "runs" | "insights" | "briefing";

const BULK_LABEL: Record<PromptStatus, string> = { vorschlag: "Zu Vorschlägen", freigegeben: "Freigeben", verworfen: "Verwerfen" };
const STATUS_DONE: Record<PromptStatus, string> = {
  vorschlag: "zu den Vorschlägen verschoben",
  freigegeben: "freigegeben",
  verworfen: "verworfen",
};

const runLabel =(run: PromptRun) => `${RUN_KIND_LABEL[run.kind]} vom ${formatDateTime(run.created_at)}`;

export function PromptsView() {
  const { project } = useShell();
  // Bei Projektwechsel neu aufbauen: Topics, Prompts und Jobs gehören zum Projekt
  return project ? <View key={project.id} project={project} /> : null;
}

function message(caught: unknown): string {
  return caught instanceof Error ? caught.message : "Unbekannter Fehler";
}

function View({ project }: { project: Project }) {
  const router = useRouter();
  const [req, setReq] = useState<PromptRequirements | null>(null);
  const [topics, setTopics] = useState<PromptTopic[] | null>(null);
  const [topicId, setTopicId] = useState<number | null>(null);
  const [items, setItems] = useState<PromptItem[]>([]);
  const [status, setStatus] = useState<PromptStatus>("vorschlag");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [checked, setChecked] = useState<Set<number>>(new Set());
  const [page, setPage] = useState(1);
  const [panel, setPanel] = useState<Panel>("none");
  const [job, setJob] = useState<RunningJob | null>(null);
  const [jobDone, setJobDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [sort, setSort] = useState<Sort>(null);
  const [filters, setFilters] = useState<Filters>(NO_FILTERS);
  const [runs, setRuns] = useState<PromptRun[] | null>(null);
  const [runsError, setRunsError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [loaded, setLoaded] = useState<{ key: string; data: PromptInsights | null; error: string | null } | null>(null);
  const [insightsStatus, setInsightsStatus] = useState<InsightsStatus>("all");
  const [briefingTag, setBriefingTag] = useState<string | null>(null);
  const lastToggled = useRef<number | null>(null);
  const attached = useRef(false);
  const progressKey = useRef("");

  const fail = useCallback(
    (caught: unknown) => {
      if (caught instanceof ApiError && caught.status === 401) {
        router.replace(loginUrl("/prompts"));
        return;
      }
      setError(message(caught));
    },
    [router],
  );

  const loadTopics = useCallback(
    () =>
      api<PromptTopic[]>(`/api/prompts/topics?project_id=${project.id}`)
        .then((list) => {
          setTopics(list);
          setTopicId((current) => (current !== null && list.some((t) => t.id === current) ? current : (list[0]?.id ?? null)));
        })
        .catch(fail),
    [project.id, fail],
  );

  const loadItems = useCallback(
    (id: number) =>
      api<PromptItem[]>(`/api/prompts/items?project_id=${project.id}&topic_id=${id}`)
        .then((list) => {
          setItems(list);
          setError(null);
        })
        .catch(fail),
    [project.id, fail],
  );

  const loadRuns = useCallback(
    () =>
      api<PromptRun[]>(`/api/prompts/runs?project_id=${project.id}`)
        .then((list) => {
          setRuns(list);
          setRunsError(null);
          return list;
        })
        .catch((caught: unknown) => {
          setRunsError(message(caught));
          return null;
        }),
    [project.id],
  );

  useEffect(() => {
    api<PromptRequirements>(`/api/prompts/requirements?project_id=${project.id}`).then(setReq).catch(fail);
    void loadTopics();
    // Ein Lauf, der noch läuft oder wartet, erscheint nach dem Neuladen der Seite wieder
    void loadRuns().then((list) => {
      if (!list || attached.current) return;
      attached.current = true;
      const open = list.find(runIsOpen);
      if (open) {
        setJob({ id: open.job_id, title: runLabel(open) });
        setJobDone(false);
      }
    });
  }, [project.id, loadTopics, loadRuns, fail]);

  useEffect(() => {
    if (topicId !== null) void loadItems(topicId);
  }, [topicId, loadItems]);

  // Die Auswertung gehört zum Topic und zur Statusauswahl: bei Wechsel neu laden, solange sie offen ist
  const insightsKey = `${topicId}:${insightsStatus}`;
  useEffect(() => {
    if (panel !== "insights" || topicId === null) return;
    const filter = insightsStatus === "all" ? "" : `&status=${insightsStatus}`;
    let current = true;
    api<PromptInsights>(`/api/prompts/insights?project_id=${project.id}&topic_id=${topicId}${filter}`)
      .then((data) => current && setLoaded({ key: insightsKey, data, error: null }))
      .catch((caught: unknown) => current && setLoaded({ key: insightsKey, data: null, error: message(caught) }));
    return () => {
      current = false;
    };
  }, [panel, topicId, insightsStatus, insightsKey, project.id]);
  const insightsNow = loaded?.key === insightsKey ? loaded : null;

  /** Topic wechseln: Auswahl, Seite und Markierung gehören zum Topic. */
  const selectTopic = (id: number) => {
    setTopicId(id);
    setChecked(new Set());
    setPage(1);
    setSelectedId(null);
    setNotice(null);
    lastToggled.current = null;
  };

  const topic = topics?.find((t) => t.id === topicId) ?? null;
  const tally = useMemo(() => counts(items), [items]);
  const inStatus = useMemo(() => ofStatus(items, status), [items, status]);
  const tags = useMemo(() => tagOptions(items), [items]);
  const shown = useMemo(() => sortItems(filterItems(inStatus, filters), sort), [inStatus, filters, sort]);
  const selected = shown.find((i) => i.id === selectedId) ?? shown[0] ?? null;

  const replace = (next: PromptItem) => setItems((list) => list.map((i) => (i.id === next.id ? next : i)));

  const patch = async (id: number, body: Record<string, unknown>): Promise<PromptItem> => {
    const next = await api<PromptItem>(`/api/prompts/items/${id}?project_id=${project.id}`, { method: "PATCH", json: body });
    replace(next);
    return next;
  };

  const decide = async (item: PromptItem, next: PromptStatus) => {
    setBusy(true);
    setActionError(null);
    // Danach springt die Auswahl zum nächsten Prompt der Ansicht (wie im Mockup)
    const position = shown.findIndex((i) => i.id === item.id);
    const following = shown[position + 1] ?? shown[position - 1] ?? null;
    try {
      await patch(item.id, { status: next });
      setSelectedId(following?.id ?? null);
      void loadTopics();
    } catch (caught) {
      setActionError(message(caught));
    } finally {
      setBusy(false);
    }
  };

  const edit = async (item: PromptItem, text: string) => {
    setBusy(true);
    try {
      await patch(item.id, { text });
    } finally {
      setBusy(false);
    }
  };

  const startJob = async (path: string, body: Record<string, unknown>, title: string) => {
    setBusy(true);
    setActionError(null);
    setNotice(null);
    try {
      const started = await api<PromptJob>(path, { method: "POST", json: { project_id: project.id, topic_id: topicId, ...body } });
      progressKey.current = "";
      setJob({ id: started.job_id, title });
      setJobDone(false);
      setPanel("none");
      void loadRuns();
    } catch (caught) {
      setActionError(message(caught));
    } finally {
      setBusy(false);
    }
  };

  const generate = (request: GenerateRequest) =>
    startJob(
      "/api/prompts/generate",
      {
        sources: request.sources,
        briefing_id: request.briefingId,
        seed: request.seed || null,
        funnel_mix: request.funnelMix,
        demand: request.demand,
        refresh_fanouts: request.refreshFanouts,
      },
      "Prompts werden generiert",
    );

  const startBriefing = async (request: { keyword: string; pageType: string }) => {
    if (topicId === null || briefingTag === null) return;
    setBusy(true);
    setActionError(null);
    try {
      const started = await api<PromptBriefingStarted>("/api/prompts/briefing", {
        method: "POST",
        json: { project_id: project.id, topic_id: topicId, tag: briefingTag, keyword: request.keyword, page_type: request.pageType },
      });
      router.push(`/briefing/${started.briefing_id}`);
    } catch (caught) {
      setActionError(message(caught));
      setBusy(false);
    }
  };

  /** Öffnet die Rückfrage zum Briefing aus einem Cluster. */
  const askBriefing = (tag: string) => {
    setActionError(null);
    setBriefingTag(tag);
    setPanel("briefing");
  };

  const createTopic = async (name: string) => {
    const created = await api<PromptTopic>("/api/prompts/topics", { method: "POST", json: { project_id: project.id, name } });
    await loadTopics();
    selectTopic(created.id);
  };

  // Nur was die Ansicht zeigt: Auswahl in anderen Reitern oder hinter einem Filter bleibt unberührt
  const chosen = shown.filter((i) => checked.has(i.id)).map((i) => i.id);

  // Nachfrage: die gewählten Prompts, ohne Auswahl alle des Topics, jeweils nur ohne Suchvolumen
  const demandPool = chosen.length > 0 ? items.filter((i) => chosen.includes(i.id)) : items;
  const demandCount = demandPool.filter((i) => i.volume === null && i.status !== "verworfen").length;

  /** Setzt den Status der gewählten Prompts in einem Schritt (freigeben, verwerfen, zu den Vorschlägen). */
  const setStatusBulk = async (next: PromptStatus) => {
    if (topicId === null || chosen.length === 0) return;
    setBusy(true);
    setActionError(null);
    setNotice(null);
    try {
      const updated = await api<PromptItem[]>("/api/prompts/items/status", {
        method: "POST",
        json: { project_id: project.id, topic_id: topicId, item_ids: chosen, status: next },
      });
      const byId = new Map(updated.map((i) => [i.id, i]));
      setItems((list) => list.map((i) => byId.get(i.id) ?? i));
      setChecked((current) => new Set([...current].filter((id) => !byId.has(id))));
      setNotice(`${formatCount(updated.length)} ${updated.length === 1 ? "Prompt" : "Prompts"} ${STATUS_DONE[next]}`);
      void loadTopics();
    } catch (caught) {
      setActionError(message(caught));
    } finally {
      setBusy(false);
    }
  };
  const finished = () => {
    setJobDone(true);
    void loadTopics();
    void loadRuns();
    if (topicId !== null) void loadItems(topicId);
  };

  /** Neue Prompts erscheinen schon während des Laufs, sobald ein Schritt fertig ist. */
  const progress = useCallback(
    (current: Job) => {
      const done = current.steps.filter((step) => step.status === "done" || step.status === "skipped").length;
      const key = `${current.id}:${done}:${current.status}`;
      if (key === progressKey.current) return;
      progressKey.current = key;
      if (current.status === "queued" || current.status === "running") setJobDone(false); // nach „Wiederholen“ läuft der Job weiter
      void loadTopics();
      void loadRuns();
      if (topicId !== null) void loadItems(topicId);
    },
    [topicId, loadTopics, loadRuns, loadItems],
  );

  const openRun = (run: PromptRun) => {
    if (run.topic_id !== null && run.topic_id !== topicId && topics?.some((t) => t.id === run.topic_id)) selectTopic(run.topic_id);
    progressKey.current = "";
    setJob({ id: run.job_id, title: runLabel(run) });
    setJobDone(false);
    setPanel("none");
  };

  const onSort = (key: SortKey) => {
    setSort((current) => nextSort(current, key));
    setPage(1);
  };
  const onFilters = (next: Filters) => {
    setFilters(next);
    setPage(1);
  };

  const exportCsv = async () => {
    if (topicId === null) return;
    try {
      const file = await apiDownload(`/api/prompts/export.csv?project_id=${project.id}&topic_id=${topicId}`, "prompts.csv");
      saveBlob(file.blob, file.filename);
    } catch (caught) {
      setActionError(message(caught));
    }
  };

  /** Umschalt-Klick wählt (oder entfernt) alle Prompts zwischen dem letzten Klick und diesem. */
  const toggle = (id: number, range = false) => {
    const order = shown.map((i) => i.id);
    const from = lastToggled.current !== null ? order.indexOf(lastToggled.current) : -1;
    const to = order.indexOf(id);
    const ids = range && from >= 0 && to >= 0 ? order.slice(Math.min(from, to), Math.max(from, to) + 1) : [id];
    lastToggled.current = id;
    setChecked((current) => {
      const next = new Set(current);
      const on = !current.has(id);
      for (const one of ids) {
        if (on) next.add(one);
        else next.delete(one);
      }
      return next;
    });
  };
  const toggleAll = (ids: number[], on: boolean) =>
    setChecked((current) => {
      const next = new Set(current);
      for (const id of ids) {
        if (on) next.add(id);
        else next.delete(id);
      }
      return next;
    });

  return (
    <div className="mx-auto flex max-w-[1400px] flex-col gap-6 px-6 py-7 sm:px-9">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-1">
          <div className="text-[13px] text-muted">Prompt-Generierung &amp; -Validierung · Projekt {project.domain}</div>
          <h1 className="text-3xl font-semibold tracking-tight">Prompts</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            aria-expanded={panel === "runs"}
            onClick={() => {
              if (panel !== "runs") void loadRuns();
              setPanel(panel === "runs" ? "none" : "runs");
            }}
            className="h-11 rounded-md border border-field bg-white px-3.5 text-sm"
          >
            Verlauf{runs && runs.length > 0 ? ` (${formatCount(runs.length)})` : ""}
          </button>
          <button
            type="button"
            disabled={topic === null}
            aria-expanded={panel === "insights"}
            onClick={() => setPanel(panel === "insights" ? "none" : "insights")}
            className="h-11 rounded-md border border-field bg-white px-3.5 text-sm disabled:opacity-60"
          >
            Auswertung
          </button>
          <button
            type="button"
            disabled={topic === null || req === null || !req.demand_available}
            title={req?.demand_reason ?? undefined}
            onClick={() => setPanel(panel === "demand" ? "none" : "demand")}
            className="h-11 rounded-md border border-field bg-white px-3.5 text-sm disabled:opacity-60"
          >
            Nachfrage
          </button>
          <button
            type="button"
            disabled={topic === null || req === null || !req.validation_available}
            onClick={() => setPanel(panel === "cluster" ? "none" : "cluster")}
            className="h-11 rounded-md border border-field bg-white px-3.5 text-sm disabled:opacity-60"
          >
            Clustern
          </button>
          <button
            type="button"
            disabled={topic === null || req === null || !req.trigger_available}
            onClick={() => setPanel(panel === "trigger" ? "none" : "trigger")}
            className="h-11 rounded-md border border-field bg-white px-3.5 text-sm disabled:opacity-60"
          >
            Trigger-Test starten
          </button>
          <button
            type="button"
            disabled={req === null}
            onClick={() => setPanel(panel === "generate" ? "none" : "generate")}
            className="h-11 rounded-md bg-accent px-3.5 text-sm font-medium text-white disabled:opacity-60"
          >
            Prompts generieren
          </button>
        </div>
      </header>

      {error && (
        <p role="alert" className="rounded-md bg-danger-bg px-4 py-3 text-sm text-danger">
          Die Prompts konnten nicht geladen werden: {error}
        </p>
      )}

      {panel === "generate" && req && (
        <GenerateBar
          req={req}
          topics={topics ?? []}
          topicId={topicId}
          onTopic={selectTopic}
          onCreateTopic={createTopic}
          onStart={generate}
          busy={busy}
          error={actionError}
        />
      )}
      {panel === "trigger" && req && (
        <TriggerDialog
          req={req}
          count={chosen.length}
          busy={busy}
          error={actionError}
          onStart={() => void startJob("/api/prompts/trigger-test", { item_ids: chosen }, "Trigger-Test läuft")}
          onCancel={() => setPanel("none")}
        />
      )}

      {panel === "demand" && req && (
        <DemandDialog
          req={req}
          count={demandCount}
          chosen={chosen.length > 0}
          busy={busy}
          error={actionError}
          onStart={() => void startJob("/api/prompts/demand", { item_ids: chosen, mode: "missing" }, "Nachfrage wird ermittelt")}
          onCancel={() => setPanel("none")}
        />
      )}

      {panel === "cluster" && (
        <ClusterDialog
          count={items.filter((i) => i.status !== "verworfen").length}
          busy={busy}
          error={actionError}
          onStart={() => void startJob("/api/prompts/cluster", {}, "Prompts werden geclustert")}
          onCancel={() => setPanel("none")}
        />
      )}

      {panel === "insights" && (
        <InsightsPanel
          insights={insightsNow?.data ?? null}
          error={insightsNow?.error ?? null}
          status={insightsStatus}
          onStatus={setInsightsStatus}
          onBriefing={askBriefing}
          onClose={() => setPanel("none")}
        />
      )}

      {panel === "briefing" && topic && briefingTag !== null && (
        <BriefingDialog
          key={briefingTag}
          topic={topic}
          tag={briefingTag}
          count={items.filter((i) => i.tag === briefingTag && i.status !== "verworfen").length}
          busy={busy}
          error={actionError}
          onStart={(request) => void startBriefing(request)}
          onCancel={() => setPanel("none")}
        />
      )}

      {panel === "runs" && (
        <RunsPanel runs={runs} error={runsError} openId={job?.id ?? null} onOpen={openRun} onClose={() => setPanel("none")} />
      )}

      {job && (
        <div className="flex flex-col gap-2">
          <JobProgress key={job.id} jobId={job.id} title={job.title} onFinished={finished} onUpdate={progress} />
          {jobDone && (
            <button type="button" onClick={() => setJob(null)} className="h-11 w-fit rounded-md border border-field bg-white px-3.5 text-sm">
              Ausblenden
            </button>
          )}
        </div>
      )}

      {topics !== null && topics.length === 0 && panel !== "generate" && (
        <p className="rounded-lg border border-line bg-white px-5 py-6 text-sm text-muted">
          Noch kein Topic. Lege über „Prompts generieren“ ein Topic an (z. B. „Elektromobilität“) und wähle die Quellen für die ersten
          Vorschläge.
        </p>
      )}

      {topic !== null && (
        <section aria-labelledby="set-title" className="rounded-lg border border-line bg-white">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#e6e9e7] px-5 py-3">
            <div className="flex flex-col gap-0.5">
              <h2 id="set-title" className="text-base font-semibold">
                {topic.name}
              </h2>
              <span className="text-xs text-muted">
                {formatCount(items.length)} Prompts · {formatCount(topic.duplicates)} Duplikate zusammengeführt
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {topics !== null && topics.length > 1 && (
                <div className="flex items-center gap-2">
                  <label htmlFor="list-topic" className="text-[13px] font-medium">
                    Topic
                  </label>
                  <select
                    id="list-topic"
                    value={topic.id}
                    onChange={(event) => selectTopic(Number(event.target.value))}
                    className="h-11 max-w-[260px] rounded-md border border-field bg-white px-3 text-sm"
                  >
                    {topics.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name} ({formatCount(t.open + t.approved + t.rejected)})
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div role="group" aria-label="Status" className="flex flex-wrap gap-1.5">
                {STATUS_ORDER.map((key) => (
                  <button
                    key={key}
                    type="button"
                    aria-pressed={status === key}
                    onClick={() => {
                      setStatus(key);
                      setPage(1);
                    }}
                    className={`h-11 rounded-full border px-3.5 text-[13px] ${
                      status === key ? "border-ink bg-ink text-white" : "border-field bg-white"
                    }`}
                  >
                    {key === "vorschlag" ? "Vorschläge" : STATUS_LABEL[key]} · {formatCount(tally[key])}
                  </button>
                ))}
              </div>
              {filters.tag !== "" && filters.tag !== NO_TAG && (
                <button
                  type="button"
                  onClick={() => askBriefing(filters.tag)}
                  className="h-11 rounded-md border border-field bg-white px-3.5 text-[13px]"
                >
                  Briefing zum Cluster starten
                </button>
              )}
              <button
                type="button"
                disabled={tally.freigegeben === 0}
                onClick={() => void exportCsv()}
                className="h-11 rounded-md border border-field bg-white px-3.5 text-[13px] disabled:opacity-60"
              >
                Freigegebene als CSV
              </button>
            </div>
          </div>

          {chosen.length > 0 && (
            <div className="flex flex-wrap items-center gap-3 border-b border-neutral-bg bg-[#f7f8f7] px-5 py-2 text-[13px]">
              <span>{formatCount(chosen.length)} ausgewählt</span>
              {STATUS_ORDER.filter((target) => target !== status).map((target) => (
                <button
                  key={target}
                  type="button"
                  disabled={busy}
                  onClick={() => void setStatusBulk(target)}
                  className={`h-11 rounded-md px-3 disabled:opacity-60 ${
                    target === "freigegeben" ? "bg-accent font-medium text-white" : "border border-field bg-white"
                  }`}
                >
                  {BULK_LABEL[target]}
                </button>
              ))}
              {chosen.length < shown.length && (
                <button type="button" onClick={() => setChecked(new Set(shown.map((i) => i.id)))} className="h-11 rounded-md px-3 text-accent underline">
                  Alle {formatCount(shown.length)} der Ansicht auswählen
                </button>
              )}
              <button
                type="button"
                disabled={busy || req === null || !req.validation_available}
                onClick={() => void startJob("/api/prompts/validate", { item_ids: chosen }, "Prompts werden validiert")}
                className="h-11 rounded-md border border-field bg-white px-3 disabled:opacity-60"
              >
                Validieren
              </button>
              <button
                type="button"
                disabled={req === null || !req.trigger_available}
                onClick={() => setPanel("trigger")}
                className="h-11 rounded-md border border-field bg-white px-3 disabled:opacity-60"
              >
                Trigger-Test …
              </button>
              <button type="button" onClick={() => setChecked(new Set())} className="h-11 rounded-md px-3 text-accent underline">
                Auswahl aufheben
              </button>
            </div>
          )}
          {notice && (
            <p role="status" className="border-b border-neutral-bg bg-success-bg px-5 py-2 text-[13px] text-success">
              {notice}
            </p>
          )}
          {actionError && panel === "none" && (
            <p role="alert" className="px-5 py-2 text-[13px] text-danger">
              {actionError}
            </p>
          )}

          <div className="flex flex-wrap">
            <div className="min-w-0 flex-[3_1_540px]">
              <PromptTable
                items={shown}
                selectedId={selected?.id ?? null}
                onSelect={setSelectedId}
                checked={checked}
                onToggle={toggle}
                onToggleAll={toggleAll}
                page={page}
                onPage={setPage}
                sort={sort}
                onSort={onSort}
                filters={filters}
                onFilters={onFilters}
                total={inStatus.length}
                tags={tags}
              />
            </div>
            {selected && req && (
              <div className="min-w-0 flex-[2_1_380px] border-l border-neutral-bg">
                <PromptDetail
                  key={selected.id}
                  item={selected}
                  busy={busy}
                  error={actionError}
                  runs={req.trigger_runs}
                  validationAvailable={req.validation_available}
                  onDecide={(next) => void decide(selected, next)}
                  onEdit={(text) => edit(selected, text)}
                  onValidate={() => void startJob("/api/prompts/validate", { item_ids: [selected.id] }, "Prompt wird validiert")}
                />
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
